import { useEffect, useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Card,
    CardContent,
    Alert,
    Chip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Link,
} from '@mui/material'
import {
    Code as CodeIcon,
    Language as WebIcon,
    Storage as ServerIcon,
    Dashboard as EmbedIcon,
    Storefront as ShopifyIcon,
} from '@mui/icons-material'
import { useSearchParams } from 'react-router-dom'
import { appPath, DASHBOARD_LINKS } from '@yourownmetric/shared'

import { useListProjectsQuery } from '@/apis/projectsApi'
import { useListApiKeysQuery, type ApiKeyRow } from '@/apis/apiKeysApi'
import { deriveV2BaseUrl } from '@/apis/v2Mappers'
import { CodeBlock } from '@/components/shared/CodeBlock'
import { trackUi } from '@/app/selfAnalytics'

import { ShopifySetupGuide } from './ShopifySetupGuide'

// ---------------------------------------------------------------------------
// Helpers — build dynamic code snippets using the user's real data
// ---------------------------------------------------------------------------

const INGEST_URL = import.meta.env.VITE_API_URL
    ? new URL(import.meta.env.VITE_API_URL as string).origin.replace('api.', 'ingest.')
    : 'https://ingest.yourownmetrics.aapastech.com'

const API_URL =
    (import.meta.env.VITE_API_URL as string) ?? 'https://api.yourownmetrics.aapastech.com/v1'

const API_V2_URL = deriveV2BaseUrl(import.meta.env.VITE_API_URL)
/** The embed bundles are served by YOM itself, from the dashboard host (cdn.ts). */
const CDN_ORIGIN =
    typeof window !== 'undefined' ? window.location.origin : 'https://yom.aapastech.com'

function snippetEmbedToken(): string {
    return `curl -X POST ${API_V2_URL}/projects/<projectId>/embed-tokens \\
  -H "Authorization: Bearer yom_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "sub_tenant": "client-42", "reports": ["overview", "timeseries", "breakdown"],
        "origins": ["app.example.com"], "ttl_seconds": 3600 }'`
}

function snippetYomReport(): string {
    return `<script src="${CDN_ORIGIN}/app/cdn/embed/latest/yom-report.iife.js" defer></script>

<yom-report project="<projectId>" token="yom_et_..." report="overview" range="30d"></yom-report>
<yom-report project="<projectId>" token="yom_et_..." report="breakdown" dim="channel" limit="8"></yom-report>`
}

const EMBED_ATTRIBUTES: Array<{ attr: string; desc: string }> = [
    { attr: 'project', desc: 'Project id (required)' },
    { attr: 'token', desc: 'yom_et_ embed token minted on your server (or set el.tokenProvider)' },
    { attr: 'report', desc: 'overview, timeseries, breakdown, ecommerce, funnel, realtime' },
    { attr: 'range / from, to', desc: 'today, 7d, 30d, 90d — or explicit YYYY-MM-DD dates' },
    {
        attr: 'metric, interval',
        desc: 'timeseries: metric (pageviews, visitors, …) and bucket size',
    },
    {
        attr: 'dim, event, limit',
        desc: 'breakdown: dimension (path, channel, country, …), event filter, rows',
    },
    {
        attr: 'kpis, compare',
        desc: 'overview: comma list of KPI keys; compare="false" hides deltas',
    },
    {
        attr: 'theme, locale, currency',
        desc: 'light / dark / auto, number formatting, ISO currency code',
    },
]

function snippetInstallNpm(): string {
    return 'npm install @yourownmetric/sdk'
}

function snippetVanillaInit(apiKey: string, domain: string): string {
    return `import { YOM } from '@yourownmetric/sdk'

YOM.init({
  apiKey: '${apiKey}',
  ingestUrl: '${INGEST_URL}',
  autoCapture: {
    pageviews: true,   // SPA-aware — handles pushState + popstate
    errors: true,      // Uncaught errors & unhandled rejections
    clicks: true,      // Outbound links, downloads, data-yom-track elements
    performance: true, // Core Web Vitals (LCP, CLS, INP, FCP, TTFB)
  },
  privacy: {
    respectDNT: true,  // Honor Do-Not-Track / Global Privacy Control
    cookieless: false,
  },
  debug: false, // Set true during development
})

// That's it! Pageviews, errors, clicks, and CWV are tracked automatically.
// Domain: ${domain}`
}

function snippetTrackEvent(): string {
    return `// Track a custom event
YOM.track('signup_completed', {
  plan: 'pro',
  source: 'landing_page',
})

// Track with revenue
YOM.track('purchase', { item: 'Premium Plan' }, {
  revenue: 49.99,
  currency: 'USD',
})`
}

function snippetIdentify(): string {
    return `// Call after user login
YOM.identify('user_abc123', {
  name: 'Jane Doe',
  email: 'jane@example.com',
  plan: 'pro',
})`
}

function snippetFlush(): string {
    return `// Flush pending events (e.g. before navigation)
await YOM.flush()

// Tear down the SDK (cleanup on app unmount)
await YOM.destroy()`
}

function snippetDataAttributes(): string {
    return `<!-- Auto-tracked by the clicks plugin -->
<button data-yom-track="cta_hero" data-yom-variant="blue">
  Get Started Free
</button>

<!-- The click plugin reads data-yom-* attributes as event properties -->
<!-- Above will fire: { name: "cta_hero", variant: "blue", tagName: "button" } -->`
}

function snippetReactInit(apiKey: string): string {
    return `import { YOMProvider, PageTracker } from '@yourownmetric/sdk/react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'

const yomConfig = {
  apiKey: '${apiKey}',
  ingestUrl: '${INGEST_URL}',
  autoCapture: {
    pageviews: true,
    errors: true,
    clicks: true,
    performance: true,
  },
  privacy: { respectDNT: true },
}

function App() {
  return (
    <YOMProvider config={yomConfig}>
      <BrowserRouter>
        <PageTracker />
        <Routes>
          {/* your routes */}
        </Routes>
      </BrowserRouter>
    </YOMProvider>
  )
}`
}

function snippetReactHooks(): string {
    return `import { useTrack, useIdentify } from '@yourownmetric/sdk/react'

function CheckoutButton() {
  const track = useTrack()
  const identify = useIdentify()

  const handlePurchase = () => {
    track('purchase', { item: 'Pro Plan' }, { revenue: 49.99, currency: 'USD' })
  }

  const handleLogin = (user) => {
    identify(user.id, { name: user.name, email: user.email })
  }

  return <button onClick={handlePurchase}>Buy Now</button>
}`
}

function snippetNodeInit(apiKey: string): string {
    return `import { YOMNode } from '@yourownmetric/sdk/node'

const yom = new YOMNode({
  apiKey: '${apiKey}',
  ingestUrl: '${INGEST_URL}',
  flushIntervalMs: 10_000, // Flush every 10s (default: 5s)
  batchSize: 50,           // Flush after 50 events (default: 20)
  maxRetries: 3,
})

// Track server-side events
yom.track('api_request', {
  endpoint: '/api/users',
  method: 'GET',
  statusCode: 200,
  durationMs: 45,
})

// Identify users from your backend
yom.identify('user_abc123', {
  name: 'Jane Doe',
  email: 'jane@example.com',
})

// IMPORTANT: Flush before process exit
process.on('SIGTERM', async () => {
  await yom.flush()
  process.exit(0)
})`
}

function snippetScriptTag(apiKey: string): string {
    return `<!-- Add this before </head>: no build step required -->
<script
  defer
  src="${CDN_ORIGIN}/app/cdn/sdk/latest/yom.min.js"
  data-key="${apiKey}"
  data-ingest="${INGEST_URL}"
  data-auto="pageviews,engagement,errors,performance"
  data-consent="unknown"
></script>`
}

function snippetEmbed(): string {
    return `<!-- Embed a YOM dashboard widget on any page -->
<script src="${CDN_ORIGIN}/app/cdn/embed/latest/yom-embed.iife.js" defer></script>

<yom-dashboard
  token="YOUR_EMBED_TOKEN"
  dashboard-id="YOUR_DASHBOARD_ID"
  api-url="${API_URL}"
  theme="auto"
></yom-dashboard>

<!-- Supports dark mode via prefers-color-scheme -->`
}

function snippetEnvVars(apiKey: string): string {
    return `# .env or .env.local
VITE_YOM_API_KEY=${apiKey}
VITE_YOM_INGEST_URL=${INGEST_URL}`
}

function snippetExistingAnalytics(apiKey: string): string {
    return `// If you already have a central analytics dispatch layer
// (e.g. sending events to GA4, FB Pixel, Mixpanel, etc.),
// add YOM alongside it:

import { YOM } from '@yourownmetric/sdk'

// 1. Initialize once at app boot
YOM.init({
  apiKey: '${apiKey}',
  ingestUrl: '${INGEST_URL}',
  autoCapture: { pageviews: true, errors: true, clicks: true, performance: true },
})

// 2. In your existing analytics utility / dispatch hub:
export function sendAnalyticsEvent(name: string, params: Record<string, unknown>) {
  // Existing: GA4 + FB Pixel
  ga4.sendEvent(name, params)
  fbPixel.trackCustom(name, params)

  // Add YOM — one extra line:
  YOM.track(name, params)
}

// That's it. Every event you already send will now also flow to YOM.`
}

// ---------------------------------------------------------------------------
// Tab Panels
// ---------------------------------------------------------------------------

interface TabPanelProps {
    value: number
    index: number
    children: React.ReactNode
}

function TabPanel({ value, index, children }: TabPanelProps) {
    return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null
}

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------

interface SectionProps {
    title: string
    subtitle?: string
    children: React.ReactNode
}

function Section({ title, subtitle, children }: SectionProps) {
    return (
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {subtitle}
                </Typography>
            )}
            {children}
        </Box>
    )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

/** Tab index → doc name (Y-APP docs_viewed). */
const DOC_TABS = ['javascript', 'react', 'node', 'embed', 'shopify'] as const

export function DocsPage() {
    // `/docs?platform=shopify` opens a tab directly (e.g. links from the website).
    const [searchParams] = useSearchParams()
    const [tab, setTab] = useState(() =>
        Math.max(0, DOC_TABS.indexOf(searchParams.get('platform') as (typeof DOC_TABS)[number])),
    )
    useEffect(() => {
        trackUi('docs_viewed', { doc: DOC_TABS[tab] ?? 'other' })
    }, [tab])
    const [selectedProjectId, setSelectedProjectId] = useState<string>('')

    const { data: projects = [] } = useListProjectsQuery()
    const { data: apiKeys = [] } = useListApiKeysQuery()

    // Resolve the active project & its first API key
    const activeProject = useMemo(
        () => projects.find(p => p.id === selectedProjectId) ?? projects[0],
        [projects, selectedProjectId],
    )

    const projectApiKey = useMemo(() => {
        if (!activeProject) {
            return ''
        }
        const key = apiKeys.find((k: ApiKeyRow) => k.projectId === activeProject.id && !k.revokedAt)
        return key ? `${key.keyPrefix}••••••••` : 'YOUR_API_KEY'
    }, [activeProject, apiKeys])

    const domain = activeProject?.domain ?? 'example.com'
    const displayKey = projectApiKey

    return (
        <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
            {/* Header */}
            <Typography variant="h4" sx={{ mb: 1 }}>
                Integration Guide
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Add privacy-first analytics to your application in under 5 minutes.
            </Typography>

            {/* Project Selector */}
            {projects.length > 1 && (
                <FormControl size="small" sx={{ mb: 3, minWidth: 260 }}>
                    <InputLabel>Show snippets for project</InputLabel>
                    <Select
                        value={selectedProjectId || activeProject?.id || ''}
                        label="Show snippets for project"
                        onChange={e => setSelectedProjectId(e.target.value)}
                    >
                        {projects.map(p => (
                            <MenuItem key={p.id} value={p.id}>
                                {p.name} ({p.domain})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {projects.length === 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You haven't created a project yet. Head to{' '}
                    <Link href={appPath('projects')} underline="hover">
                        Projects
                    </Link>{' '}
                    to create one, then come back here for personalized setup instructions.
                </Alert>
            )}

            {activeProject && projectApiKey === 'YOUR_API_KEY' && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No active API key found for <strong>{activeProject.name}</strong>. Go to{' '}
                    <Link href={DASHBOARD_LINKS.apiKeys} underline="hover">
                        API Keys
                    </Link>{' '}
                    to create one — the snippets below will auto-populate with your real key.
                </Alert>
            )}

            {/* SDK Info Banner */}
            <Card sx={{ mb: 3, bgcolor: '#f0f0ff', border: '1px solid #c7d2fe' }}>
                <CardContent
                    sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
                >
                    <Chip label="@yourownmetric/sdk" color="primary" size="small" />
                    <Typography variant="body2" color="text.secondary">
                        v0.1.0 • ~4 KB gzipped • Zero dependencies • Tree-shakeable
                    </Typography>
                </CardContent>
            </Card>

            {/* Framework Tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v as number)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    mb: 0,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
                }}
            >
                <Tab icon={<WebIcon />} iconPosition="start" label="JavaScript" />
                <Tab icon={<CodeIcon />} iconPosition="start" label="React" />
                <Tab icon={<ServerIcon />} iconPosition="start" label="Node.js" />
                <Tab icon={<EmbedIcon />} iconPosition="start" label="Embed Widget" />
                <Tab icon={<ShopifyIcon />} iconPosition="start" label="Shopify" />
            </Tabs>
            <Divider />

            {/* ---- JavaScript / Vanilla Tab ---- */}
            <TabPanel value={tab} index={0}>
                <Section title="1. Install the SDK">
                    <CodeBlock code={snippetInstallNpm()} title="Terminal" language="bash" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Or use a CDN — no build step required:
                    </Typography>
                    <CodeBlock
                        code={snippetScriptTag(displayKey)}
                        title="index.html"
                        language="html"
                    />
                </Section>

                <Section
                    title="2. Initialize"
                    subtitle="Call this once at app boot. Auto-capture handles the rest."
                >
                    <CodeBlock
                        code={snippetVanillaInit(displayKey, domain)}
                        title="analytics.ts"
                        language="typescript"
                    />
                </Section>

                <Section title="3. Environment Variables (recommended)">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Keep your API key out of source code:
                    </Typography>
                    <CodeBlock code={snippetEnvVars(displayKey)} title=".env" />
                </Section>

                <Section
                    title="4. Track Custom Events"
                    subtitle="Fire-and-forget — events are batched automatically."
                >
                    <CodeBlock
                        code={snippetTrackEvent()}
                        title="anywhere in your app"
                        language="typescript"
                    />
                </Section>

                <Section
                    title="5. Identify Users"
                    subtitle="Link anonymous sessions to authenticated users."
                >
                    <CodeBlock code={snippetIdentify()} title="after login" language="typescript" />
                </Section>

                <Section
                    title="6. HTML Data Attributes"
                    subtitle="Track clicks without writing any JavaScript."
                >
                    <CodeBlock code={snippetDataAttributes()} title="your HTML" language="html" />
                </Section>

                <Section
                    title="7. Lifecycle"
                    subtitle="Flush events before navigation or on app unmount."
                >
                    <CodeBlock code={snippetFlush()} language="typescript" />
                </Section>

                <Section
                    title="Integrating into an existing analytics layer?"
                    subtitle="If you already have a centralized dispatch for GA4, FB Pixel, etc., you can add YOM with a single line."
                >
                    <CodeBlock
                        code={snippetExistingAnalytics(displayKey)}
                        title="your-analytics-hub.ts"
                        language="typescript"
                    />
                </Section>
            </TabPanel>

            {/* ---- React Tab ---- */}
            <TabPanel value={tab} index={1}>
                <Section title="1. Install">
                    <CodeBlock code={snippetInstallNpm()} title="Terminal" language="bash" />
                </Section>

                <Section
                    title="2. Wrap Your App with YOMProvider"
                    subtitle="The provider initializes the SDK and cleans up on unmount. PageTracker handles SPA route changes."
                >
                    <CodeBlock code={snippetReactInit(displayKey)} title="App.tsx" language="tsx" />
                </Section>

                <Section
                    title="3. Use Hooks"
                    subtitle="Track events and identify users from any component."
                >
                    <CodeBlock
                        code={snippetReactHooks()}
                        title="components/CheckoutButton.tsx"
                        language="tsx"
                    />
                </Section>

                <Section title="4. Available Hooks">
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {[
                                    {
                                        hook: 'useTrack()',
                                        desc: 'Returns a track(name, properties?, options?) function',
                                    },
                                    {
                                        hook: 'useIdentify()',
                                        desc: 'Returns an identify(userId, traits?) function',
                                    },
                                    {
                                        hook: 'usePageView(path?)',
                                        desc: 'Tracks a pageview on mount / when path changes',
                                    },
                                ].map(({ hook, desc }) => (
                                    <Box
                                        key={hook}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: 'primary.main',
                                                minWidth: 180,
                                            }}
                                        >
                                            {hook}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {desc}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Section>

                <Section title="5. Components">
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {[
                                    {
                                        comp: '<YOMProvider config={...}>',
                                        desc: 'Context provider — initializes the SDK, tears down on unmount',
                                    },
                                    {
                                        comp: '<PageTracker />',
                                        desc: 'Drop-in component — tracks pageviews on every route change',
                                    },
                                ].map(({ comp, desc }) => (
                                    <Box
                                        key={comp}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: 'primary.main',
                                            }}
                                        >
                                            {comp}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {desc}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Section>
            </TabPanel>

            {/* ---- Node.js Tab ---- */}
            <TabPanel value={tab} index={2}>
                <Section title="1. Install">
                    <CodeBlock code={snippetInstallNpm()} title="Terminal" language="bash" />
                </Section>

                <Section
                    title="2. Initialize the Server-Side Client"
                    subtitle="The Node client batches events in memory and flushes on a timer."
                >
                    <CodeBlock
                        code={snippetNodeInit(displayKey)}
                        title="server/analytics.ts"
                        language="typescript"
                    />
                </Section>

                <Section title="3. Important Notes">
                    <Card>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Alert severity="info" icon={false}>
                                <strong>Always flush before exit</strong> — The Node client buffers
                                events in memory. Call <code>yom.flush()</code> on{' '}
                                <code>SIGTERM</code> / <code>SIGINT</code> to avoid losing events.
                            </Alert>
                            <Alert severity="info" icon={false}>
                                <strong>One instance per process</strong> — Create the YOMNode
                                instance once and import it where needed. Don't create multiple
                                instances.
                            </Alert>
                            <Alert severity="info" icon={false}>
                                <strong>Server-side only</strong> — Import from{' '}
                                <code>@yourownmetric/sdk/node</code>, not the default entry point.
                                The default export is browser-only.
                            </Alert>
                        </CardContent>
                    </Card>
                </Section>
            </TabPanel>

            {/* ---- Embed Widget Tab ---- */}
            <TabPanel value={tab} index={3}>
                <Section
                    title="Embed a Dashboard Widget"
                    subtitle="Display analytics data on any webpage using the <yom-dashboard> Web Component."
                >
                    <CodeBlock code={snippetEmbed()} title="your-page.html" language="html" />
                </Section>

                <Section title="Attributes">
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {[
                                    {
                                        attr: 'token',
                                        desc: 'Embed token (create one in Settings → API Keys with "read" scope)',
                                        required: true,
                                    },
                                    {
                                        attr: 'dashboard-id',
                                        desc: 'The ID of the dashboard to display',
                                        required: true,
                                    },
                                    {
                                        attr: 'api-url',
                                        desc: `API base URL (defaults to ${API_URL})`,
                                        required: false,
                                    },
                                    {
                                        attr: 'theme',
                                        desc: '"light", "dark", or "auto" (follows system)',
                                        required: false,
                                    },
                                ].map(({ attr, desc, required }) => (
                                    <Box
                                        key={attr}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                color: 'primary.main',
                                                minWidth: 140,
                                            }}
                                        >
                                            {attr}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {desc}
                                        </Typography>
                                        {required && (
                                            <Chip
                                                label="required"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Section>
            </TabPanel>

            {/* ---- Shopify Tab (custom pixel, no app) ---- */}
            <TabPanel value={tab} index={4}>
                <Section title="Shopify: step-by-step setup">
                    <ShopifySetupGuide
                        projectId={activeProject?.id}
                        projectName={activeProject?.name}
                        ingestUrl={INGEST_URL}
                    />
                </Section>
            </TabPanel>

            {/* ---- Client portals, scheduled reports, embeds ---- */}
            <Divider sx={{ my: 4 }} />

            <Section
                title="Client portals & scheduled reports"
                subtitle="White-label reporting for agencies and platforms — each client sees only their own sub-tenant."
            >
                <Card>
                    <CardContent sx={{ display: 'grid', gap: 1.5 }}>
                        <Typography variant="body2">
                            <strong>Portals</strong> (Manage → Client portals, owners/admins): your
                            product name, logo and colors, the reports clients may see, and a
                            sign-in at <code>/portal/&lt;slug&gt;</code> — or on your own domain
                            (paid plans) after adding the DNS records shown in the portal&apos;s
                            Custom domain card.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Client logins</strong> are invited by email and bound to one or
                            more sub-tenant ids; with several they get a client switcher. The invite
                            link can also be copied and shared directly.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Scheduled reports</strong> email a KPI digest every Monday (last
                            week) or on the 1st (last month) at 06:00 in the project&apos;s timezone
                            — for one client or the whole project, optionally with a portal&apos;s
                            branding and a CSV attachment. Preview or send one any time. Free plan:
                            1 portal, 3 client logins, 1 schedule with 3 recipients, no CSV,
                            &ldquo;Powered by&rdquo; footer always shown.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            API: <code>/v2/projects/:id/portals</code>,{' '}
                            <code>/portals/:portalId/users</code>,{' '}
                            <code>/portals/:portalId/domain</code> and{' '}
                            <code>/v2/projects/:id/scheduled-reports</code> (dashboard session or an
                            admin/write API key).
                        </Typography>
                    </CardContent>
                </Card>
            </Section>

            <Section
                title="Embeds (<yom-report>)"
                subtitle="Put live reports inside your own app with a dependency-free Web Component and short-lived embed tokens (paid plans)."
            >
                <Typography variant="body2" sx={{ mb: 1 }}>
                    1. Mint a token on your server with a secret key — bind it to the signed-in
                    user&apos;s sub-tenant, allowed reports and page origins (max 24 h):
                </Typography>
                <CodeBlock code={snippetEmbedToken()} title="mint an embed token" language="bash" />
                <Typography variant="body2" sx={{ my: 1 }}>
                    2. Render reports with the token:
                </Typography>
                <CodeBlock code={snippetYomReport()} title="your-page.html" language="html" />
                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {EMBED_ATTRIBUTES.map(({ attr, desc }) => (
                                <Box
                                    key={attr}
                                    sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            color: 'primary.main',
                                            minWidth: 190,
                                        }}
                                    >
                                        {attr}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 2 }}
                        >
                            React:{' '}
                            <code>
                                import {'{ YomReport }'} from &apos;@yourownmetric/embed/react&apos;
                            </code>
                            . Full reference (theming, events, token refresh):{' '}
                            <code>docs/embed.md</code> in the YOM repository.
                        </Typography>
                    </CardContent>
                </Card>
            </Section>

            {/* ---- Auto-capture Reference ---- */}
            <Divider sx={{ my: 4 }} />

            <Section
                title="What's Captured Automatically"
                subtitle="When autoCapture is enabled, the SDK tracks these events out-of-the-box — zero code required."
            >
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {[
                                {
                                    feature: 'Pageviews',
                                    flag: 'pageviews: true',
                                    desc: 'Tracks every page navigation, SPA-aware (pushState + popstate)',
                                },
                                {
                                    feature: 'Errors',
                                    flag: 'errors: true',
                                    desc: 'Captures uncaught errors and unhandled promise rejections',
                                },
                                {
                                    feature: 'Clicks',
                                    flag: 'clicks: true',
                                    desc: 'Outbound links, file downloads, buttons with names/IDs, data-yom-track elements',
                                },
                                {
                                    feature: 'Web Vitals',
                                    flag: 'performance: true',
                                    desc: 'LCP, CLS, FID/INP, FCP, TTFB — rated good/needs-improvement/poor per Google thresholds',
                                },
                                {
                                    feature: 'Sessions',
                                    flag: 'always on',
                                    desc: 'session_start / session_end events with 30-min idle timeout, anonymous ID via localStorage',
                                },
                                {
                                    feature: 'UTM Parameters',
                                    flag: 'always on',
                                    desc: 'Automatically parsed from URL query string (utm_source, utm_medium, utm_campaign, etc.)',
                                },
                            ].map(({ feature, flag, desc }) => (
                                <Box key={feature}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            {feature}
                                        </Typography>
                                        <Chip
                                            label={flag}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontFamily: 'monospace', fontSize: 11 }}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            </Section>

            {/* ---- Privacy Section ---- */}
            <Section title="Privacy & Compliance">
                <Card>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant="body2">
                            <strong>Do-Not-Track / GPC:</strong> When{' '}
                            <code>privacy.respectDNT: true</code> is set, the SDK checks{' '}
                            <code>navigator.doNotTrack</code> and{' '}
                            <code>navigator.globalPrivacyControl</code>. If either is enabled,
                            tracking is disabled entirely.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Cookieless mode:</strong> Set{' '}
                            <code>privacy.cookieless: true</code> to disable localStorage-based
                            anonymous IDs. Each page load gets a fresh random ID.
                        </Typography>
                        <Typography variant="body2">
                            <strong>No PII by default:</strong> The SDK never captures form inputs,
                            passwords, or personal data. User identification is opt-in via{' '}
                            <code>YOM.identify()</code>.
                        </Typography>
                    </CardContent>
                </Card>
            </Section>

            {/* ---- Batching Section ---- */}
            <Section title="Batching & Delivery">
                <Card>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant="body2">
                            Events are buffered in memory and sent in batches. Default:{' '}
                            <strong>batch of 20</strong> or <strong>every 5 seconds</strong>,
                            whichever comes first.
                        </Typography>
                        <Typography variant="body2">
                            On page close (<code>visibilitychange → hidden</code>), remaining events
                            are sent via <code>navigator.sendBeacon()</code> for reliable delivery.
                        </Typography>
                        <Typography variant="body2">
                            Failed requests retry with exponential backoff (up to 3 retries).
                            Permanently failed events (4xx except 429) are dropped.
                        </Typography>
                    </CardContent>
                </Card>
            </Section>

            {/* ---- Troubleshooting ---- */}
            <Section title="Troubleshooting">
                <Card>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {[
                            {
                                q: 'Events not showing up in the dashboard?',
                                a: 'Enable debug: true in your config. Check the browser console for [YOM] messages. Verify your API key is correct and not revoked.',
                            },
                            {
                                q: 'CORS errors?',
                                a: `The ingest endpoint (${INGEST_URL}) accepts requests from any origin. If you're using a custom domain, make sure it matches your project's configured domain.`,
                            },
                            {
                                q: 'Events delayed?',
                                a: 'Events are batched (default: 20 events or 5s). In development, you can reduce flushIntervalMs to 1000 for faster feedback.',
                            },
                            {
                                q: 'Duplicate pageviews in SPA?',
                                a: "If you have autoCapture.pageviews enabled AND you're manually calling YOM.page(), you'll get duplicates. Choose one approach.",
                            },
                        ].map(({ q, a }) => (
                            <Box key={q}>
                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                    {q}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {a}
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            </Section>
        </Box>
    )
}
